import { Outlet, Link, useLocation } from 'react-router-dom';
import { Home, Users as SheepIcon, Receipt, TrendingUp, Package, Baby, FileText, Users, LogOut, Menu, X, Settings, Bell, GitBranch, Mail, History, ChevronDown, ChevronRight, Sun, Moon, Monitor } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

const ThemeToggle = ({ themeMode, cycleTheme, darkMode }) => {
  const icon = themeMode === 'light' ? Sun : themeMode === 'dark' ? Moon : Monitor;
  const Icon = icon;
  const label = themeMode === 'light' ? 'Clair' : themeMode === 'dark' ? 'Sombre' : 'Système';
  return (
    <button
      onClick={cycleTheme}
      title={`Thème : ${label}`}
      data-testid="theme-toggle-button"
      className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
        darkMode ? 'text-gray-400 hover:bg-gray-900' : 'text-gray-500 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden lg:inline text-xs">{label}</span>
    </button>
  );
};

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { darkMode, themeMode, cycleTheme } = useTheme();

  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('nav_sections');
    return saved ? JSON.parse(saved) : { gestion: true, suivi: true, admin: true };
  });

  useEffect(() => {
    localStorage.setItem('nav_sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isDevEnvironment = process.env.REACT_APP_BACKEND_URL?.includes('preview.emergentagent.com');

  const navCategories = [
    {
      key: 'gestion',
      label: 'Gestion',
      items: [
        { name: 'Tableau de bord', path: '/', icon: Home },
        { name: 'Troupeau', path: '/troupeau', icon: SheepIcon },
        { name: 'Naissances', path: '/naissances', icon: Baby },
        { name: 'Achats/Ventes', path: '/transactions', icon: TrendingUp },
        { name: 'Dépenses', path: '/depenses', icon: Receipt },
        { name: 'Stock', path: '/stock', icon: Package },
      ],
    },
    {
      key: 'suivi',
      label: 'Suivi & Rapports',
      items: [
        { name: 'Rapports', path: '/rapports', icon: FileText },
        ...(user?.role === 'admin' ? [{ name: 'Alertes', path: '/alertes', icon: Bell }] : []),
      ],
    },
    ...(user?.role === 'admin' ? [{
      key: 'admin',
      label: 'Administration',
      items: [
        { name: 'Templates Email', path: '/templates-email', icon: Mail },
        { name: 'Historique Tâches', path: '/historique-taches', icon: History },
        { name: 'Utilisateurs', path: '/utilisateurs', icon: Users },
        ...(isDevEnvironment ? [{ name: 'CI/CD', path: '/ci-status', icon: GitBranch }] : []),
      ],
    }] : []),
  ];

  const standaloneItems = [
    { name: 'Paramètres', path: '/parametres', icon: Settings },
  ];

  const renderNavLink = (item, isActive, onClick) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.name.toLowerCase().replace(/[\s/]+/g, '-')}`}
        className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all ${
          isActive
            ? darkMode ? 'bg-slate-700 text-white' : 'bg-slate-800 text-white shadow-sm'
            : darkMode ? 'text-gray-400 hover:bg-gray-900 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <Icon className={`w-5 h-5 ${isActive ? 'text-white' : darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        <span className="font-medium text-sm">{item.name}</span>
      </Link>
    );
  };

  const renderSidebar = (onItemClick) => (
    <>
      <nav className="space-y-1">
        {navCategories.map((cat) => {
          if (cat.items.length === 0) return null;
          const isExpanded = expandedSections[cat.key] !== false;
          const hasActiveItem = cat.items.some(item => location.pathname === item.path);
          return (
            <div key={cat.key} className="mb-1">
              <button
                onClick={() => toggleSection(cat.key)}
                data-testid={`nav-section-${cat.key}`}
                className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors ${
                  darkMode ? 'text-gray-600 hover:text-gray-400 hover:bg-gray-900/60' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100/60'
                } ${hasActiveItem && !isExpanded ? (darkMode ? 'text-slate-400' : 'text-slate-600') : ''}`}
              >
                <span>{cat.label}</span>
                {isExpanded
                  ? <ChevronDown className="w-3.5 h-3.5" />
                  : <ChevronRight className="w-3.5 h-3.5" />
                }
              </button>
              {isExpanded && (
                <div className="mt-0.5 space-y-0.5">
                  {cat.items.map((item) => renderNavLink(item, location.pathname === item.path, onItemClick))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        {standaloneItems.map((item) => renderNavLink(item, location.pathname === item.path, onItemClick))}
      </div>
    </>
  );

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-black' : 'bg-slate-50'}`}>
      {/* Top Navigation */}
      <nav className={`${darkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} border-b sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <span className="text-xl">🐑</span>
              </div>
              <div>
                <h1 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Firma</h1>
              </div>
            </Link>

            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-1">
              <div className="text-right mr-3">
                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user?.nom}</p>
                <p className={`text-xs capitalize ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{user?.role}</p>
              </div>
              <ThemeToggle themeMode={themeMode} cycleTheme={cycleTheme} darkMode={darkMode} />
              <button
                onClick={onLogout}
                className={`flex items-center space-x-2 px-3 py-2 text-sm text-red-500 ${darkMode ? 'hover:bg-red-950/50' : 'hover:bg-red-50'} rounded-lg transition-colors`}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>

            {/* Mobile */}
            <div className="md:hidden flex items-center gap-1">
              <ThemeToggle themeMode={themeMode} cycleTheme={cycleTheme} darkMode={darkMode} />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-900 text-white' : 'hover:bg-gray-100'}`}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className={`hidden md:block w-60 min-h-screen ${darkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} border-r p-3`}>
          {renderSidebar(undefined)}
        </aside>

        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setMobileMenuOpen(false)}>
            <aside className={`w-64 min-h-screen ${darkMode ? 'bg-black' : 'bg-white'} p-4`} onClick={(e) => e.stopPropagation()}>
              <div className="mb-6">
                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user?.nom}</p>
                <p className={`text-xs capitalize ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{user?.role}</p>
              </div>
              {renderSidebar(() => setMobileMenuOpen(false))}
              <button
                onClick={onLogout}
                className={`flex items-center space-x-2 px-4 py-3 mt-4 text-sm text-red-500 ${darkMode ? 'hover:bg-red-950/50' : 'hover:bg-red-50'} rounded-lg w-full transition-colors`}
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden min-w-0">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
